import { useCallback, useEffect, useRef, useState } from 'react';

type UseInfiniteScrollOptions<T> = {
  /** Reference to the scrollable container */
  containerRef: React.RefObject<HTMLElement | null>;
  /**
   * Fetches the next items. An offset fetcher returns the items and a short page
   * ends the list; a cursor fetcher ignores the offset and says `hasMore` itself,
   * since a full step can still be the last one.
   */
  fetchMore: (offset: number) => Promise<T[] | { hasMore: boolean; items: T[] } | undefined>;
  /** Number of items per page */
  pageSize?: number;
  /** Distance from top (in px) to trigger loading */
  threshold?: number;
  /** Whether the scroll is enabled */
  enabled?: boolean;
  /** Direction of infinite scroll */
  direction?: 'up' | 'down';
};

type UseInfiniteScrollResult = {
  /** Whether more items are currently being loaded */
  isLoading: boolean;
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Current pagination offset */
  offset: number;
  /** Reset the scroll state (call when data source changes) */
  reset: () => void;
  /** Manually trigger loading more items */
  loadMore: () => Promise<void>;
  /** Set initial offset after first load */
  setInitialOffset: (offset: number) => void;
  /** Mark that there are no more items */
  setHasMore: (hasMore: boolean) => void;
};

export const useInfiniteScroll = <T>({
  containerRef,
  fetchMore,
  pageSize = 100,
  threshold = 200,
  enabled = true,
  direction = 'up',
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  // Use refs to avoid stale closures in scroll handler
  const isLoadingRef = useRef(isLoading);
  const hasMoreRef = useRef(hasMore);
  const offsetRef = useRef(offset);
  const fetchMoreRef = useRef(fetchMore);

  const previousScrollHeightRef = useRef<number>(0);
  const isRestoringScrollRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  }, [fetchMore]);

  const reset = useCallback(() => {
    setIsLoading(false);
    setHasMore(true);
    setOffset(0);
    isLoadingRef.current = false;
    hasMoreRef.current = true;
    offsetRef.current = 0;
    previousScrollHeightRef.current = 0;
  }, []);

  const setInitialOffset = useCallback((newOffset: number) => {
    setOffset(newOffset);
    offsetRef.current = newOffset;
  }, []);

  const setHasMoreState = useCallback((value: boolean) => {
    setHasMore(value);
    hasMoreRef.current = value;
  }, []);

  const loadMore = useCallback(async () => {
    const container = containerRef.current;
    if (!enabled || isLoadingRef.current || !hasMoreRef.current || !container) return;

    setIsLoading(true);
    isLoadingRef.current = true;

    if (direction === 'up') {
      previousScrollHeightRef.current = container.scrollHeight;
    }

    try {
      const result = await fetchMoreRef.current(offsetRef.current);
      const exhausted = Array.isArray(result) ? result.length < pageSize : !result?.hasMore;

      if (exhausted) {
        setHasMore(false);
        hasMoreRef.current = false;
      }

      setOffset((prev) => prev + pageSize);
      offsetRef.current = offsetRef.current + pageSize;
    } catch (error) {
      console.error('[useInfiniteScroll] Failed to load more:', error);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [containerRef, enabled, pageSize, direction]);

  // Scroll event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const handleScroll = () => {
      if (isLoadingRef.current || !hasMoreRef.current || isRestoringScrollRef.current) return;

      if (direction === 'up') {
        if (container.scrollTop < threshold) {
          void loadMore();
        }
      } else {
        const distanceFromBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceFromBottom < threshold) {
          void loadMore();
        }
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [containerRef, enabled, threshold, direction, loadMore]);

  // Scroll position restoration (for direction='up')
  useEffect(() => {
    const container = containerRef.current;
    if (!container || direction !== 'up') return;
    if (isLoading) return;

    if (previousScrollHeightRef.current > 0) {
      isRestoringScrollRef.current = true;

      const newScrollHeight = container.scrollHeight;
      const scrollDiff = newScrollHeight - previousScrollHeightRef.current;
      container.scrollTop = scrollDiff;
      previousScrollHeightRef.current = 0;

      // Allow scroll events again after restoration
      requestAnimationFrame(() => {
        isRestoringScrollRef.current = false;
      });
    }
  }, [containerRef, direction, isLoading, offset]);

  return {
    isLoading,
    hasMore,
    offset,
    reset,
    loadMore,
    setInitialOffset,
    setHasMore: setHasMoreState,
  };
};
